import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError, setStoredToken, getStoredToken, setStoredRefresh } from "@/lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      // If there's a stale token in localStorage, purge it so the
      // next paint drops the user cleanly to the login page instead
      // of thrashing between /auth/me and the app shell.
      if (getStoredToken()) setStoredToken(null);
      setStoredRefresh(null);
      setUser(false);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const onExpired = () => {
      setUser((u) => {
        if (u) toast.error("Your session expired. Please sign in again — nothing was saved from the last action.");
        return false;
      });
    };
    window.addEventListener("bsvac:session-expired", onExpired);
    return () => window.removeEventListener("bsvac:session-expired", onExpired);
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      // Persist the bearer token so browsers that dropped the cookie
      // (iOS Safari ITP, incognito, in-app webviews) can still auth
      // on subsequent requests via the axios interceptor.
      if (data?.access_token) setStoredToken(data.access_token);
      if (data?.refresh_token) setStoredRefresh(data.refresh_token);
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      if (data?.access_token) setStoredToken(data.access_token);
      if (data?.refresh_token) setStoredRefresh(data.refresh_token);
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setStoredToken(null);
    setStoredRefresh(null);
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, ready, login, register, logout, refresh, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
